// RRULE UTILS
import {RRule, rrulestr} from 'rrule';
import moment from 'moment/moment';
import {get, isEmpty, forEach} from 'lodash';

const DaysOfWeekMap = {
    1: RRule.MO,
    2: RRule.TU,
    3: RRule.WE,
    4: RRule.TH,
    5: RRule.FR,
    6: RRule.SA,
    7: RRule.SU,
};

export const getFrequency = (sessState) => {
    switch (sessState?.customIntervalType) {
        case 'day':
            return RRule.DAILY;
        case 'week':
            return RRule.WEEKLY;
        default:
        case 'month':
            return RRule.MONTHLY;
    }
};

const getDefaultIntervalDigit = (sessState) => {
    switch (sessState?.intervalType) {
        case 'week':
            return 1;
        case '2week':
            return 2;
        case '3week':
            return 3;
        case '4week':
            return 4;
        default:
            return 1;
    }
};

const getDaysOfWeekInterval = (sessState) => {
    const daysOfWeekArr = [];

    sessState.daysOfWeek.forEach((day) => daysOfWeekArr.push(DaysOfWeekMap[day]));

    return daysOfWeekArr;
};

export const modifyRruleString = (sessState) => {
    if (!sessState.rrule) return '';

    const rruleObj = RRule.fromString(sessState.rrule);

    const newEndDate = moment(sessState.endDate).toDate();

    const updatedRrule = new RRule({
        dtstart: rruleObj.options.dtstart,
        byweekday: rruleObj.options.byweekday,
        freq: rruleObj.options.freq,
        interval: rruleObj.options.interval,
        until: newEndDate,
    });

    return updatedRrule.toString();
}

const getDefaultInterval = (sessState) => {
    const number = getDefaultIntervalDigit(sessState);

    return new RRule({
        interval: number,
        freq: RRule.WEEKLY,
        until: moment(sessState.endDate).toDate(),
        dtstart: moment(sessState.startDate).toDate(),
        byweekday: getDaysOfWeekInterval(sessState),
    });
};

const getCustomInterval = (sessState) => {
    const number = parseInt(sessState?.customIntervalNumber, 10) || 1;

    return new RRule({
        freq: getFrequency(sessState),
        interval: number,
        until: moment(sessState.endDate).toDate(),
        dtstart: moment(sessState.startDate).toDate(),
        byweekday: getDaysOfWeekInterval(sessState),
    });
};

export const getIntervalRrule = (sessionState) => {
    if (!!sessionState?.customInterval) {
        return getCustomInterval(sessionState);
    } else {
        return getDefaultInterval(sessionState);
    }
};

export const getSlotsForSession = (session, from, to) => {
    const result = [];
    if (!session?.rrule) return;
    let rule = rrulestr(session.rrule);
    let sessStart = moment(session.startDate).set({second:0,millisecond:0});
    let slotEnd = sessStart.clone().add(session.slotDurationInMinutes,"minute")
    let sessEnd = moment(session.startDate).set({hour:moment(session.endDate).hour(),minute:moment(session.endDate).minute(),second:0,millisecond:0});
    let slotHours = [];

    while(slotEnd.isSameOrBefore(sessEnd, 'minute')) {
        slotHours.push({hour:sessStart.hour(),minute:sessStart.minute(),second:0,millisecond:0});
        slotEnd = slotEnd.add(session.slotDurationInMinutes,"minute")
        sessStart = sessStart.add(session.slotDurationInMinutes,"minute")
    }
    const start = from;
    const end = to;

    const eventDates = rule.between(
        start.toDate(),
        end.toDate(),
        true,
    );

    session.daySlots = {};
    session.outsideSlotsTemplate = session.outsideSlotsTemplate || [];

    if (!isEmpty(eventDates)) {
        // Temporary set to collect unique minutes-of-day for outside slots
        const outsideMinutesSet = new Set();

        forEach(eventDates, dt => {
            const dtDate = moment(dt);
            let slotdate =  dtDate.format('YYYY_MM_DD');

            // Generate IN-SESSION slots
            session.daySlots[slotdate] = [];
            forEach(slotHours, h=> {
                session.daySlots[slotdate].push({startDate: dtDate.clone().set(h) ,assignedSession:session.sessionId});
            });

            if (!session.outsideSlotsTemplate || session.outsideSlotsTemplate.length === 0) {
                // Generate OUTSIDE-SESSION slots for this day and collect minute offsets
                const dayStart = dtDate.clone().startOf('day');
                const dayEnd = dtDate.clone().endOf('day');

                const sessionStart = dtDate.clone().set({
                    hour: moment(session.startDate).hour(),
                    minute: moment(session.startDate).minute(),
                    second: 0,
                    millisecond: 0,
                });

                const sessionEnd = dtDate.clone().set({
                    hour: moment(session.endDate).hour(),
                    minute: moment(session.endDate).minute(),
                    second: 0,
                    millisecond: 0,
                });

                let timeMoment = sessionStart.clone().subtract(session.slotDurationInMinutes, 'minutes');

                while (timeMoment.isSameOrAfter(dayStart, 'minute')) {
                    // collect minute-of-day for template
                    outsideMinutesSet.add(timeMoment.hour() * 60 + timeMoment.minute());
                    timeMoment.subtract(session.slotDurationInMinutes, 'minutes');
                }

                // Slots AFTER session
                timeMoment = sessionEnd.clone();

                while (timeMoment.isBefore(dayEnd, 'minute')) {
                    // collect minute-of-day for template
                    outsideMinutesSet.add(timeMoment.hour() * 60 + timeMoment.minute());
                    timeMoment.add(session.slotDurationInMinutes, 'minutes');
                }

                // Build template only once (if not already present)
                const minutesArr = Array.from(outsideMinutesSet).sort((a,b) => a-b);
                session.outsideSlotsTemplate = minutesArr.map(min => ({
                    minutesFromDayStart: min,
                    assignedSession: session.sessionId,
                    outsideSession: true,
                }));
            }
        });
    }

    return result;
};

export const printDaysOfWeek = (rruleString, printFormat = 'dddd') => {
    const rule = rrulestr(rruleString);
    const ruleOptions = rule.origOptions;

    if (ruleOptions.byweekday) {
        const dayArr = ruleOptions.byweekday.map((day) =>
            moment()
                .isoWeekday(day.weekday + 1)
                .format(printFormat),
        );

        return dayArr.join(', ');
    } else {
        return '-';
    }
};
